import { useState, useEffect } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  ScatterController,
  CategoryScale,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";

ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels,
  ScatterController
);

export default function IntonationPlot({ intensity }) {
  const [showDerivative, setShowDerivative] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const xs = intensity?.pitch?.xs || [];
  const ys = intensity?.pitch?.ys || [];
  const dYsOriginal = intensity?.pitch_1st_derivative?.ys || [];

  if (!xs.length) return null;

  const yMin = Math.min(...ys);
  const yMax = Math.max(...ys);
  const yRange = yMax - yMin;

  // 🔹 Local maxima detection
  const localMaxima = [];
  for (let i = 1; i < ys.length - 1; i++) {
    if (ys[i] > ys[i - 1] && ys[i] > ys[i + 1]) {
      localMaxima.push({ x: xs[i], y: ys[i] });
    }
  }

  const datasets = [];

  // 🔷 Main pitch line - optimized for mobile
  datasets.push({
    label: "Pitch (Hz)",
    data: xs.map((x, i) => ({ x, y: ys[i] })),
    borderColor: "#2563eb",
    borderWidth: isMobile ? 1.5 : 2,
    tension: isMobile ? 0.2 : 0.4,
    pointRadius: 0,
    showLine: true,
    datalabels: { display: false },
  });

  // 🟣 Scale derivative to match pitch visually
  let dYsScaled = [];
  if (dYsOriginal.length === xs.length) {
    const dAbsMax = Math.max(...dYsOriginal.map((v) => Math.abs(v))) || 1;
    const scaleFactor = (yRange * 0.5) / dAbsMax;
    const verticalShift = yMin + yRange * 0.25;
    dYsScaled = dYsOriginal.map((v) => v * scaleFactor + verticalShift);
  }

  if (showDerivative && dYsScaled.length === xs.length) {
    datasets.push({
      label: "Pitch Derivative (scaled)",
      data: xs.map((x, i) => ({ x, y: dYsScaled[i] })),
      borderColor: "purple",
      borderDash: [4, 4],
      borderWidth: isMobile ? 1 : 2,
      tension: isMobile ? 0.2 : 0.4,
      pointRadius: 0,
      showLine: true,
      spanGaps: true,
      datalabels: { display: false },
    });
  }

  // 🔴 Local maxima - optimized for mobile
  datasets.push({
    label: "Local Maxima Line",
    data: localMaxima,
    borderColor: "red",
    borderWidth: isMobile ? 1 : 2,
    tension: isMobile ? 0.2 : 0.4,
    pointRadius: isMobile ? 2 : 4,
    pointBackgroundColor: "red",
    pointBorderColor: "red",
    showLine: true,
    datalabels: { display: false },
  });

  const data = { datasets };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    devicePixelRatio: isMobile ? 1 : Math.max(2, window.devicePixelRatio),
    animation: false,
    scales: {
      x: {
        type: "linear",
        grid: { display: true, color: "rgba(0,0,0,0.05)" },
        ticks: { display: false },
        title: { 
          display: !isMobile, 
          text: "Time (seconds)",
          font: { size: isMobile ? 12 : 14 }
        }
      },
      y: {
        min: 0,
        max: yMax + yRange * 0.1,
        grid: { color: "rgba(0,0,0,0.05)" },
        title: { 
          display: true, 
          text: "Pitch (Hz)", 
          font: { size: isMobile ? 12 : 16 } 
        },
        ticks: { 
          font: { size: isMobile ? 10 : 14 } 
        },
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: !isMobile,
        mode: "nearest", 
        intersect: true 
      },
      datalabels: { display: false },
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    }
  };

  return (
    <div className="flex flex-col gap-2 w-full min-w-0">
      {/* ✅ Toggle for derivative - mobile optimized */}
      <label className={`flex items-center gap-2 select-none ${isMobile ? 'text-xs' : 'text-sm'}`}>
        <input
          type="checkbox"
          checked={showDerivative}
          onChange={() => setShowDerivative(!showDerivative)}
          className={isMobile ? 'scale-90' : ''}
        />
        Show Pitch 1st Derivative (scaled)
      </label>

      {/* 📉 Chart wrapper: responsive and mobile optimized */}
      <div className={`w-full min-w-0 overflow-x-auto bg-white rounded-lg border border-gray-200 p-2 ${
        isMobile ? 'h-48' : 'h-64'
      }`}>
        <Line data={data} options={options} />
      </div>

      {/* Mobile info text */}
      {isMobile && (
        <p className="text-xs text-gray-500 text-center">
          Pinch to zoom for details
        </p>
      )}
    </div>
  );
}